"use client";

import React from "react";
import { Button } from "../common/button";

export interface TherapistProfileSidebarLabels {
  bookSession?: string;
  joinWaitlist?: string;
  jumpToSection?: string;
  navAbout?: string;
  navExpertise?: string;
  navLogistics?: string;
}

export interface TherapistProfileSidebarProps {
  firstName: string;
  lastName: string;
  displayName: string;
  credential: string;
  profileImage: string;
  acceptingClients: boolean;
  therapistTier?: 'resident' | 'associate';
  /** Override the displayed tier label. Default keeps the existing
   *  "Intern Therapist" / "Associate Therapist" copy used elsewhere
   *  on alli.io — apps that brand differently (e.g. imwell uses
   *  "Resident Therapist") can pass overrides here. */
  tierLabels?: { resident?: string; associate?: string };
  /** Raw pronoun value from the pros table. Rendered underneath the
   *  display name when present + non-opt-out. */
  pronoun?: string;
  /** Optional label overrides for user-visible strings. Each field
   *  defaults to English; consumers that need translations (imwell)
   *  pass a `labels` prop built from their i18n bundle. Other apps
   *  can omit and get the original copy. */
  labels?: TherapistProfileSidebarLabels;
  onCtaClick?: () => void;
}

// Normalizes the pronoun string coming out of the DB. Returns null when
// the value should be suppressed (opt-out or empty). Handles the messy
// historic values (`prefer_not_to_say`, `ask-me`, `any-pronouns`) that
// pre-date the current picker.
function formatPronoun(raw?: string): string | null {
  if (!raw) return null;
  const v = raw.trim();
  if (!v) return null;
  const lower = v.toLowerCase();
  if (lower === 'prefer_not_to_say' || lower === 'prefer-not-to-say') return null;
  if (lower === 'ask-me' || lower === 'ask_me') return 'ask me';
  if (lower === 'any-pronouns' || lower === 'any_pronouns') return 'any pronouns';
  return v;
}

export function TherapistProfileSidebar({
  firstName,
  lastName,
  displayName,
  credential,
  profileImage,
  acceptingClients,
  therapistTier,
  tierLabels,
  pronoun,
  labels,
  onCtaClick
}: TherapistProfileSidebarProps) {
  const tierLabel = therapistTier
    ? therapistTier === 'resident'
      ? tierLabels?.resident ?? 'Intern Therapist'
      : tierLabels?.associate ?? 'Associate Therapist'
    : null;
  const displayPronoun = formatPronoun(pronoun);
  const bookSessionLabel = labels?.bookSession ?? 'Book a Session';
  const joinWaitlistLabel = labels?.joinWaitlist ?? 'Join Waitlist';
  const jumpToSectionLabel = labels?.jumpToSection ?? 'Jump to Section';
  const navAboutLabel = labels?.navAbout ?? 'About Me';
  const navExpertiseLabel = labels?.navExpertise ?? 'How I Help';
  const navLogisticsLabel = labels?.navLogistics ?? 'Practice Information';
  return (
    <div className="lg:sticky lg:top-8 lg:z-10">
      <div className="space-y-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6">
            <img 
              src={profileImage}
              alt={displayName}
              className="w-[220px] h-[288px] object-cover mx-auto rounded-xl mb-6"
            />
            
            <div className="text-center mb-6">
              <h1 className="text-xl font-bold text-gray-900 mb-1">
                {displayName}
              </h1>
              {displayPronoun && (
                <p className="text-sm text-gray-500 italic mb-1">{displayPronoun}</p>
              )}
              {credential && (
                <p className="text-sm text-gray-600 mb-1">{credential}</p>
              )}
              {tierLabel && (
                <p className="text-sm text-gray-500">{tierLabel}</p>
              )}
            </div>
            
            {/* CTA Button */}
            {acceptingClients ? (
              <Button variant="primary" size="lg" className="w-full" onClick={onCtaClick}>
                {bookSessionLabel}
              </Button>
            ) : (
              <Button variant="secondary" size="lg" className="w-full border-cyan-700 shadow-none hover:border-cyan-700" onClick={onCtaClick}>
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {joinWaitlistLabel}
              </Button>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">{jumpToSectionLabel}</h3>
          <div className="space-y-2">
            {[
              { id: "about", label: navAboutLabel, icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" },
              { id: "expertise", label: navExpertiseLabel, icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" },
              { id: "logistics", label: navLogisticsLabel, icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" }
            ].map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  const element = document.getElementById(section.id);
                  element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors duration-200 border border-transparent hover:border-primary-200"
              >
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={section.icon} />
                </svg>
                <span className="font-medium">{section.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}